/**
 * Manager Quick Start Guide
 *
 * Daily workflow guide for managers showing what to do and when.
 * Part of the DOC-06 role-specific quick start guides.
 */

export const managerQuickStartContent = {
  id: 'quickstart-manager',
  title: 'Manager Quick Start Guide',
  category: 'quickstart',
  roles: ['manager'],
  keywords: [
    'getting started', 'manager', 'team', 'performance', 'first steps',
    'how to start', 'new manager', 'onboarding', 'tutorial', 'verification', 'team oversight'
  ],

  sections: [
    {
      title: 'Welcome, Manager!',
      content: `As a manager, you oversee team leaders and verify their pulse entries for your assigned gyms.

**Your main responsibilities:**
- Monitor your team's pulse submission status
- Verify entries submitted by your team leaders
- Support TLs in meeting their commitments
- Track your gyms' performance`
    },
    {
      title: 'Your Morning Routine',
      content: `Start your day by checking on your team's status.

**Check Manager View:**
- **Go to:** Manager View
- **Look for:** Team member status - who has submitted, who is pending
- **Action:** Note any TLs who haven't logged in yet

**Review Your Gyms:**
- **Go to:** Command Center (you'll see your assigned gyms)
- **Look for:** Any red badges or low fill rates from yesterday
- **Action:** Follow up with TLs about unresolved issues`
    },
    {
      title: 'After Each Pulse Period',
      content: `Your main task throughout the day is verifying pulse entries as they come in.

| Pulse | Time Window | TLs Should Submit By | You Verify By |
|-------|-------------|---------------------|---------------|
| P1 | 8:00-12:00 | 12:00 PM | 12:30 PM |
| P2 | 12:00-16:00 | 4:00 PM | 4:30 PM |
| P3 | 16:00-20:00 | 8:00 PM | 8:30 PM |
| P4 | 20:00-22:00 | 10:00 PM | 10:30 PM |

**Verify Entries:**
- **Go to:** Verification page
- **Look for:** Entries marked "TL Verified" from your gyms
- **Action:** Review the numbers, then click Verify to approve

**What to check before verifying:**
1. Do the numbers look reasonable? (Not drastically different from typical)
2. Are closures filled in with proper details?
3. Is the entry for the correct pulse period?

**Check Pulse Operations:**
- **Go to:** Pulse Operations
- **Check:** Your gyms' metrics and fill rates
- **Action:** Follow up with TLs who haven't submitted`
    },
    {
      title: 'End of Day',
      content: `Ensure all entries are submitted and verified before wrapping up.

**Ensure All Verified:**
- **Go to:** Manager View
- **Check:** All TLs have submitted for all 4 pulse periods
- **Verify:** You've verified all entries (no pending items)
- **Action:** Follow up on any missing entries

**Review Commitments:**
- **Go to:** Commitment Dashboard
- **Check:** Your gyms' commitment status
- **Action:** Support TLs in fulfilling any pending commitments

**Daily Review Questions:**
- Did all my TLs submit on time today?
- Are there any unfulfilled commitments?
- Which TL needs extra support?`
    },
    {
      title: 'Quick Links',
      links: [
        { label: 'Manager View', path: '/manager-view', description: 'Team overview and status' },
        { label: 'Verification', path: '/verification', description: 'Verify TL pulse entries' },
        { label: 'Pulse Operations', path: '/pulse-operations', description: 'Monitor pulse fill rates' },
        { label: 'Commitment Dashboard', path: '/commitment-dashboard', description: 'Track closures and fulfillment' },
        { label: 'Command Center', path: '/', description: 'Overall gym performance' }
      ]
    },
    {
      title: 'Pro Tips',
      content: `**Work more efficiently:**

1. **Keep the Verification page open** - Refresh periodically after each pulse window ends

2. **Set reminders** - Add calendar alerts 15 minutes after each pulse window ends (12:15, 4:15, 8:15, 10:15)

3. **Communicate proactively** - If a TL is consistently late, have a conversation about time management

4. **Use Manager View for quick status** - It shows at a glance who has submitted and who hasn't

5. **Check commitment status daily** - Don't let unfulfilled commitments pile up

**Common manager questions:**
- "My TL forgot to submit - what do I do?" - They can still submit late, but it will be flagged. Remind them to submit ASAP.
- "The numbers look wrong - should I reject?" - Talk to the TL first. You can only verify or skip, not reject.
- "How do I see my team's historical performance?" - Use the Command Center drill-down feature`
    }
  ]
};

export default managerQuickStartContent;
